/**
 * Recipe Share Helper
 * Generate shareable recipe links with tracking
 */

/**
 * Generate shareable recipe URL
 */
export function getShareableRecipeUrl(recipeId) {
  const baseUrl = window.location.origin;
  return `${baseUrl}/recipe/${recipeId}`;
}

/**
 * Share recipe with native share or copy link
 */
export async function shareRecipeLink(recipe) {
  const shareUrl = getShareableRecipeUrl(recipe.id);
  const recipeName = recipe.name || recipe.title || 'Recipe';
  
  const shareData = {
    title: recipeName,
    // Just the URL - let the app's link preview show the details
    url: shareUrl
  };

  console.log('📤 Sharing recipe:', { recipeName, shareUrl });

  // Track share (optional analytics)
  trackRecipeShare(recipe.id, 'link');

  // Try native share
  if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
    try {
      await navigator.share(shareData);
      console.log('✅ Shared via native share');
      return { success: true, method: 'native' };
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('⚠️ Share cancelled by user');
        return { success: false, cancelled: true };
      }
      console.log('⚠️ Native share failed, falling back to clipboard');
      // Fall through to copy
    }
  }

  // Fallback: copy to clipboard
  try {
    await navigator.clipboard.writeText(shareUrl);
    console.log('✅ Link copied to clipboard');
    return { success: true, method: 'clipboard', url: shareUrl };
  } catch (error) {
    console.error('❌ Failed to copy to clipboard:', error);
    return { success: false, error: error.message, url: shareUrl };
  }
}

/**
 * Copy recipe link to clipboard
 */
export async function copyRecipeLink(recipeId) {
  const shareUrl = getShareableRecipeUrl(recipeId);
  
  try {
    await navigator.clipboard.writeText(shareUrl);
    trackRecipeShare(recipeId, 'copy');
    return { success: true, url: shareUrl };
  } catch (error) {
    return { success: false, error: error.message, url: shareUrl };
  }
}

/**
 * Track recipe shares (for analytics)
 */
function trackRecipeShare(recipeId, method) {
  // Log to console (in production, you'd send to analytics service)
  console.log('📊 Recipe shared:', {
    recipeId,
    method,
    timestamp: new Date().toISOString()
  });

  // Store in localStorage for basic tracking
  try {
    const shares = JSON.parse(localStorage.getItem('recipeShares') || '[]');
    shares.push({
      recipeId,
      method,
      timestamp: new Date().toISOString()
    });
    // Keep only last 100 shares
    if (shares.length > 100) shares.shift();
    localStorage.setItem('recipeShares', JSON.stringify(shares));
  } catch (error) {
    console.error('Error tracking share:', error);
  }
}

/**
 * Get share count for recipe
 */
export function getRecipeShareCount(recipeId) {
  try {
    const shares = JSON.parse(localStorage.getItem('recipeShares') || '[]');
    return shares.filter(s => s.recipeId === recipeId).length;
  } catch (error) {
    return 0;
  }
}

/**
 * Get popular recipes by share count
 */
export function getPopularRecipes() {
  try {
    const shares = JSON.parse(localStorage.getItem('recipeShares') || '[]');
    const counts = {};
    
    shares.forEach(share => {
      counts[share.recipeId] = (counts[share.recipeId] || 0) + 1;
    });

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([recipeId, count]) => ({ recipeId, count }));
  } catch (error) {
    return [];
  }
}

/**
 * Generate social media share links
 */
export function getSocialShareLinks(recipe) {
  const shareUrl = encodeURIComponent(getShareableRecipeUrl(recipe.id));
  const recipeName = encodeURIComponent(recipe.name || recipe.title || 'Recipe');
  const description = encodeURIComponent(recipe.description || '');

  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${recipeName}`,
    pinterest: recipe.photoUrl || recipe.photoURL ? 
      `https://pinterest.com/pin/create/button/?url=${shareUrl}&media=${encodeURIComponent(recipe.photoUrl || recipe.photoURL)}&description=${description}` :
      null,
    whatsapp: `https://wa.me/?text=${recipeName}%20${shareUrl}`,
    email: `mailto:?subject=${recipeName}&body=${description}%0A%0A${shareUrl}`
  };
}

/**
 * Check if recipe can be shared (public/family settings)
 */
export function canShareRecipe(recipe) {
  // For now, all recipes can be shared
  // In future, you might add privacy settings
  return true;
}

